'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-card.scss';
import { useTranslation } from 'react-i18next';
import { useRandomChild } from '@sola/ark-core/contexts/random-child.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { FlexibleContentProps, SacFilterProps } from '@sola/schema/lib/schema.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { Image } from '../../atoms/image/image.tsx';
import { ChildBioSummary } from '../child-bio-details/child-bio-summary.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { NavCardSkeleton } from './nav-card-skeleton.tsx';
type NavCardProps = FlexibleContentProps & {
  featured?: boolean;
  className?: string;
};
type NavCardSacProps = SacFilterProps & {
  featured?: boolean;
  className?: string;
};
const NavCardContent: React.FC<NavCardProps> = t0 => {
  const $ = _c(20);
  let className;
  let featured;
  let media;
  let props;
  if ($[0] !== t0) {
    const {
      media: t1,
      subtitle: _subtitle,
      icon: _icon,
      quotation: _quotation,
      featured: t2,
      className: t3,
      ...t4
    } = t0;
    media = t1;
    featured = t2;
    className = t3;
    props = t4;
    $[0] = t0;
    $[1] = className;
    $[2] = featured;
    $[3] = media;
    $[4] = props;
  } else {
    className = $[1];
    featured = $[2];
    media = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("NavCard w-full", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== featured || $[8] !== media?.image) {
    t2 = media?.image && <div className="NavCard__media"><Image className={cn("aspect-ratio-1 lg:aspect-ratio-3-4 xl:aspect-ratio-1", {
        "aspect-ratio-4-3 lg:aspect-ratio-3-4 xl:aspect-ratio-4-3": featured
      })} image={media?.image} hasPlaceholder={true} objectPosition="top" rounded="md" /></div>;
    $[7] = featured;
    $[8] = media?.image;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = !featured ? props.title : undefined;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("NavCard__body");
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== t3) {
    t5 = <FeatureBase {...props} title={t3} titleSize="lg" titleVariant="label" bodySize="md" bodyVariant="label" buttonSize="md" primaryCTAVariant="tertiary" primaryCTAIconName="arrow-right" primaryCTAIconPlacement="after" className={t4} variantSpacing="card" alignment="left" inline={true} buttonsAsLinks={true} />;
    $[11] = props;
    $[12] = t3;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t2 || $[15] !== t5) {
    t6 = <div className="NavCard__content flex items-start gap-comfy-lg lg:gap-comfy-sm">{t2}{t5}</div>;
    $[14] = t2;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t6) {
    t7 = <div className={t1}>{t6}</div>;
    $[17] = t1;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
const NavCardSac = props => {
  const $ = _c(9);
  const {
    className
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["bio"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    data: childData,
    isLoading
  } = useRandomChild();
  let t1;
  if ($[1] !== childData || $[2] !== props || $[3] !== t) {
    t1 = childData ? {
      title: childData.name.preferred,
      body: <ChildBioSummary className="NavCard__body" short={true} details={childData} />,
      media: {
        image: {
          url: childData.imageUrl,
          height: 96,
          width: 70,
          alt: t("bio:picAlt", {
            childName: childData.name.preferred
          })
        }
      },
      primaryCTA: {
        title: t("bio:title", {
          childName: childData.name.preferred
        }),
        link: `${knownSkippyUrls.childBio}?gid=${childData.globalId}`
      }
    } : props;
    $[1] = childData;
    $[2] = props;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const cardProps = t1;
  let t2;
  if ($[5] !== cardProps || $[6] !== className || $[7] !== isLoading) {
    t2 = isLoading ? <NavCardSkeleton className={className} /> : <NavCardContent {...cardProps} className={className} />;
    $[5] = cardProps;
    $[6] = className;
    $[7] = isLoading;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const NavCard = t0 => {
  const $ = _c(6);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  if ("limit" in props) {
    let t1;
    if ($[2] !== props) {
      t1 = <NavCardSac {...props} />;
      $[2] = props;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== props) {
    t1 = <NavCardContent {...props} />;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};